import { client as axios } from "@smfa/utils/src";
import { store } from "../store";
import { get } from "lodash";

// Explicitly mark these as public routes
const PUBLIC_API = true;

/**
 * Get home page content
 * @returns {Promise} Home page content
 */
export const getHomePageContent = () => {
  // Dispatch action to indicate request in progress
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "home" },
  });

  return axios
    .get("/homepage", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (get(response, "data.status")) {
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "home",
            content: response.data.data,
          },
        });
      }
      return response;
    })
    .catch((error) => {
      store.dispatch({
        type: "FETCH_PAGE_CONTENT_FAILURE",
        payload: error.message || "Failed to load home page content",
      });
      throw error;
    });
};

export const getAboutPageContent = () => {
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "about" },
  });
  return axios
    .get("/aboutpage", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (get(response, "data.status")) {
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "about",
            content: response.data?.data?.about || {},
          },
        });
      }
      return response;
    });
};

export const getPrivacyPolicyContent = (persist = false) => {
  if (persist) {
    store.dispatch({
      type: "FETCH_PAGE_CONTENT_REQUEST",
      payload: { page: "privacypolicy" },
    });
  }
  return axios
    .get("/privacy-policy", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (persist && get(response, "data.status")) {
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "privacypolicy",
            content: response.data?.data || {},
          },
        });
      }
      return response?.data?.data;
    });
};

export const getCancelPolicyContent = (persist = false) => {
  if (persist) {
    store.dispatch({
      type: "FETCH_PAGE_CONTENT_REQUEST",
      payload: { page: "cancelpolicy" },
    });
  }
  return axios
    .get("/cancel-policy", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (persist && get(response, "data.status")) {
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "cancelpolicy",
            content: response.data?.data || {},
          },
        });
      }
      return response?.data?.data;
    });
};

/**
 * Get Committees, Affiliates, and Membership pages content
 * Returns data for multiple pages from a single API endpoint
 * @returns {Promise} API response
 */
export const getComAffMemPageContent = () => {
  // Dispatch loading state for all pages this endpoint will populate
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "committees" },
  });

  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "affiliates" },
  });

  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "membership" },
  });

  return axios
    .get("/com-af-mem-pages", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (get(response, "data.status")) {
        // Extract specific sections of data for each page
        const data = response.data;

        // Dispatch for committees page
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "committees",
            content: {
              key: "committees",
              data: {
                committee: data.data?.commiteepage || {},
              },
            },
          },
        });
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "affiliates",
            content: {
              key: "affiliates",
              data: data.data?.affiliatepage || {},
            },
          },
        });
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "membership",
            content: {
              key: "membership",
              data: data.data?.membershipbpage || {},
            },
          },
        });
      }

      return response;
    })
    .catch((error) => {
      const errorMessage = error.message || "Failed to load page content";
      store.dispatch({
        type: "FETCH_PAGE_CONTENT_FAILURE",
        payload: errorMessage,
      });

      throw error;
    });
};

/**
 * Get all data for meetings, patients, and contact us pages
 * @returns {Promise} API response
 */
export const getMeetPatConPageContent = () => {
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "meetings" },
  });
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "patients" },
  });
  store.dispatch({
    type: "FETCH_PAGE_CONTENT_REQUEST",
    payload: { page: "contactus" },
  });
  return axios
    .get("/meet-pat-con-pages", { meta: { publicRoute: PUBLIC_API } })
    .then((response) => {
      if (get(response, "data.status")) {
        const data = response.data;
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "meetings",
            content: {
              key: "meetings",
              data: data.data?.meetingbpage || {},
            },
          },
        });
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "patients",
            content: {
              key: "patients",
              data: data.data?.patientpage || {},
            },
          },
        });
        store.dispatch({
          type: "FETCH_PAGE_CONTENT_SUCCESS",
          payload: {
            page: "contactus",
            content: {
              key: "contactus",
              data: data.data?.contactpage || {},
            },
          },
        });
      }

      return response;
    })
    .catch((error) => {
      // Dispatch failure for all three pages
      const errorMessage = error.message || "Failed to load page content";

      store.dispatch({
        type: "FETCH_PAGE_CONTENT_FAILURE",
        payload: errorMessage,
      });

      throw error;
    });
};

// Combined action creator that loads all page content
export const fetchAllPageContent = async (dispatch) => {
  try {
    dispatch({ type: "FETCH_PAGE_CONTENT_REQUEST" });
    // Fetch all content types in parallel
    const [homeData, aboutData, memberData, meetingData] = await Promise.all([
      getHomePageContent(),
      getAboutPageContent(),
      getComAffMemPageContent(),
      getMeetPatConPageContent(),
    ]);

    if (homeData?.data?.status === true) {
      dispatch(setPageContent({ page: "home", content: homeData.data.data }));
    }

    if (aboutData?.data?.status === true) {
      dispatch(setPageContent({ page: "about", content: aboutData.data.data }));
    }

    if (memberData?.data?.status === true) {
      dispatch(
        setPageContent({
          page: "committees",
          content: memberData.data.data,
        }),
      );
    }

    if (meetingData?.data?.status === true) {
      dispatch(
        setPageContent({
          page: "meetings",
          content: meetingData.data.data,
        }),
      );
    }

    dispatch({
      type: "FETCH_PAGE_CONTENT_SUCCESS",
      payload: { timestamp: new Date().toISOString() },
    });
  } catch (error) {
    log.error("fetchAllPageContent() error:", error);
    dispatch({ type: "FETCH_PAGE_CONTENT_FAILURE", payload: error.message });
  }
};

// Helper function to import on demand
export const setPageContent = (payload) => ({
  type: "pageContents/setPageContent",
  payload,
});

export const getformupmeet = (id) =>
  axios.get("/downloadupmeeting/" + id, {
    responseType: "blob",
  });

export const getformprevmeet = (id) =>
  axios.get("/downloadpremeeting/" + id, {
    responseType: "blob",
  });

export const contactUsSubmit = (data) => axios.post("/contactform", data);

export const onlineApplicationSubmit = (data) =>
  axios.post("/onlineapplication", data);

export const registationSubmit = (data) => axios.post("/registation", data);

export const getExistingRegistation = (data) =>
  axios.post("/existing-registation", data);

// Meetings

export const getMeetings = (data) => axios.get("/meetings", data);
export const getMeeting = (id) => axios.get("/meetings/" + id);

// Fetch all meetings
export const fetchMeetings = () => {
  return (dispatch) => {
    dispatch({ type: "FETCH_PAGE_CONTENT_REQUEST" });

    return axios
      .get("/meetings", { meta: { publicRoute: PUBLIC_API } })
      .then((response) => {
        if (get(response, "data.status")) {
          dispatch({
            type: "FETCH_MEETINGS_SUCCESS",
            payload: { meetings: response.data.data },
          });
          return response.data.data;
        } else {
          throw new Error(response.data.message || "Failed to fetch meetings");
        }
      })
      .catch((error) => {
        dispatch({
          type: "FETCH_PAGE_CONTENT_FAILURE",
          payload: error.message,
        });
        throw error;
      });
  };
};

// Fetch a specific meeting
export const fetchMeeting = (id) => {
  return (dispatch) => {
    dispatch({ type: "FETCH_PAGE_CONTENT_REQUEST" });

    return axios
      .get(`/meetings/${id}`, { meta: { publicRoute: PUBLIC_API } })
      .then((response) => {
        if (get(response, "data.status")) {
          dispatch({
            type: "FETCH_MEETING_SUCCESS",
            payload: { meeting: response.data.data },
          });
          return response.data.data;
        } else {
          throw new Error(
            response.data.message || "Failed to fetch meeting details",
          );
        }
      })
      .catch((error) => {
        dispatch({
          type: "FETCH_PAGE_CONTENT_FAILURE",
          payload: error.message,
        });
        throw error;
      });
  };
};

// Get meeting document
export const getMeetingDocument = (id, documentName) => {
  return axios
    .get(`/meetings/${id}/document`, {
      params: { document: documentName },
      responseType: "blob",
      meta: { publicRoute: PUBLIC_API },
    })
    .then((response) => {
      return response.data;
    })
    .catch((error) => {
      log.error("Error downloading document:", error);
      throw new Error("Failed to fetch document");
    });
};

const pageContentActions = {
  getHomePageContent,
  getAboutPageContent,
  getComAffMemPageContent,
  getMeetPatConPageContent,
};

export default pageContentActions;
